import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RequirementCategory } from "../../types";
import { UserCheck, CheckCircle2, XCircle, Trash2, X, Tag } from "lucide-react";

interface BulkActionBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkAssign: () => void;
  onBulkCategory: (category: RequirementCategory) => void;
  onBulkToggleMandatory: (mandatory: boolean) => void;
  onBulkConfirm: (confirmed: boolean) => void;
  onBulkDelete: () => void;
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  selectedCount,
  onClearSelection,
  onBulkAssign,
  onBulkCategory,
  onBulkToggleMandatory,
  onBulkConfirm,
  onBulkDelete,
}) => {
  return (
    <AnimatePresence>
      {selectedCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 15 }}
          transition={{ duration: 0.18 }}
          className="sticky bottom-4 z-30 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-300 bg-white/95 p-3.5 shadow-xl backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95"
        >
          {/* Left: count + clear */}
          <div className="flex items-center gap-2.5">
            <span className="flex h-6 items-center rounded-full bg-[#2ba5b5] px-2.5 text-xs font-bold text-white">
              {selectedCount}
            </span>
            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
              requirement{selectedCount === 1 ? "" : "s"} selected
            </span>
            <button
              type="button"
              onClick={onClearSelection}
              className="inline-flex items-center gap-1 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
              title="Clear selection"
            >
              <X className="h-3.5 w-3.5" />
              <span className="text-[11px]">Clear</span>
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Assign Staff */}
            <button
              type="button"
              onClick={onBulkAssign}
              className="btn-animate inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <UserCheck className="h-3.5 w-3.5 text-[#2ba5b5]" />
              <span>Assign Staff</span>
            </button>

            {/* Change Category Dropdown */}
            <div className="relative inline-flex items-center">
              <Tag className="pointer-events-none absolute left-2.5 h-3 w-3 text-slate-400" />
              <select
                aria-label="Bulk change category"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    onBulkCategory(e.target.value as RequirementCategory);
                    e.target.value = "";
                  }
                }}
                className="rounded-lg border border-slate-200 bg-white py-1.5 pl-7 pr-3 text-xs font-semibold text-slate-700 outline-hidden hover:bg-slate-50 focus:border-[#2ba5b5] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                <option value="" disabled>
                  Set Category...
                </option>
                <option value="technical">Technical</option>
                <option value="qualifications">Qualifications</option>
                <option value="pricing">Pricing</option>
                <option value="management">Management</option>
                <option value="compliance">Compliance</option>
                <option value="general">General</option>
              </select>
            </div>

            {/* Mandatory / Optional */}
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-800">
              <button
                type="button"
                onClick={() => onBulkToggleMandatory(true)}
                className="btn-animate rounded-md px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Mandatory
              </button>
              <button
                type="button"
                onClick={() => onBulkToggleMandatory(false)}
                className="btn-animate rounded-md px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
              >
                Optional
              </button>
            </div>

            {/* Confirm / Unconfirm */}
            <button
              type="button"
              onClick={() => onBulkConfirm(true)}
              className="btn-animate inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400"
              title="Mark all selected as confirmed"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>Confirm</span>
            </button>
            <button
              type="button"
              onClick={() => onBulkConfirm(false)}
              className="btn-animate inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
              title="Mark all selected as unconfirmed"
            >
              <XCircle className="h-3.5 w-3.5 text-slate-400" />
              <span>Unconfirm</span>
            </button>

            {/* Bulk Delete */}
            <button
              type="button"
              onClick={onBulkDelete}
              className="btn-animate inline-flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-400"
              title="Delete all selected requirements"
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span>Delete</span>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
